import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/ToastContext';
import {
	createFoundItem,
	createItemReturn,
	deleteFoundItem,
	getCategories,
	getFoundItem,
	getFoundItemCounts,
	getFoundItems,
	updateFoundItem,
} from '../lib/api';
import type { FoundItemsParams, FoundItem } from '../types';

export const foundItemsKeys = {
	all: ['found-items'] as const,
	list: (params: FoundItemsParams) => [...foundItemsKeys.all, 'list', params] as const,
	detail: (id: string) => [...foundItemsKeys.all, 'detail', id] as const,
};

export const categoriesKeys = {
	all: ['categories'] as const,
	list: () => [...categoriesKeys.all, 'list'] as const,
};

export function useFoundItems(params: FoundItemsParams = {}) {
	return useQuery({
		queryKey: foundItemsKeys.list(params),
		queryFn: () => getFoundItems(params),
		placeholderData: (previous) => previous,
	});
}

export function useFoundItem(id: string) {
	return useQuery({
		queryKey: foundItemsKeys.detail(id),
		queryFn: () => getFoundItem(id),
		enabled: !!id,
	});
}

export function useCategories() {
	return useQuery({
		queryKey: categoriesKeys.list(),
		queryFn: getCategories,
		staleTime: 5 * 60 * 1000,
	});
}

export function useCreateFoundItem() {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	return useMutation({
		mutationFn: createFoundItem,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: foundItemsKeys.all });
			addToast('success', 'Item criado com sucesso');
		},
		onError: (error) => {
			addToast('error', error instanceof Error ? error.message : 'Erro ao criar item');
		},
	});
}

export function useUpdateFoundItem() {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<FoundItem> }) =>
			updateFoundItem(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: foundItemsKeys.all });
			addToast('success', 'Item atualizado com sucesso');
		},
		onError: (error) => {
			addToast('error', error instanceof Error ? error.message : 'Erro ao atualizar item');
		},
	});
}

export function useDeleteFoundItem() {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	return useMutation({
		mutationFn: deleteFoundItem,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: foundItemsKeys.all });
			addToast('success', 'Item excluído com sucesso');
		},
		onError: (error) => {
			addToast('error', error instanceof Error ? error.message : 'Erro ao excluir item');
		},
	});
}

export function useCreateItemReturn() {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	return useMutation({
		mutationFn: createItemReturn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: foundItemsKeys.all });
			addToast('success', 'Item marcado como devolvido');
		},
		onError: (error) => {
			addToast('error', error instanceof Error ? error.message : 'Erro ao registrar devolução');
		},
	});
}

export function useFoundItemCounts() {
	return useQuery({
		queryKey: [...foundItemsKeys.all, 'counts'],
		queryFn: getFoundItemCounts,
		staleTime: 30 * 1000,
	});
}
